import { FC } from 'react';

import { DOTS } from '@components/constants';
import { usePagination } from '@components/hooks/usePagination/usePagination';
import { usePaginationLoader } from '@components/hooks/usePaginationLoader/usePaginationLoader';
import { PaginationProps } from './Pagination.types';
import { ArrowIconStyled, PaginationContainerStyled, PaginationItemStyled } from './Pagintion.styles';
import { useTranslation } from 'react-i18next';
import { COMMON_TRANSLATION } from '@/common/constants';

const Pagination: FC<PaginationProps> = (props: PaginationProps) => {
  const { t } = useTranslation(COMMON_TRANSLATION);
  const {
    onPageChange,
    totalCount = 0,
    siblingCount = 1,
    currentPage,
    pageSize,
    hasMorePages,
    continuosPagination = false,
  } = props;

  const MIN_PAGINATION_RANGE_LENGTH = totalCount === 0 ? 1 : 2;

  /**
    SampleTracking, ViewOrders --> usePaginationLoader
    ListTeams --> usePagination
    Both hooks are used to generate the pagination range based on the parent list, 
    due to *hasMorePages* is not going to be applied to all list views
  */
  const paginationRange = continuosPagination
    ? usePaginationLoader({
        currentPage,
        pageSize,
        hasMorePages,
      })
    : usePagination({
        currentPage,
        totalCount,
        siblingCount,
        pageSize,
      });

  const onNext = (): void => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = (): void => {
    onPageChange(currentPage - 1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, pageNumber: number): void => {
    if (event.key === 'Enter') {
      onPageChange(pageNumber);
    }
  };

  const lastPage = paginationRange !== undefined && paginationRange[paginationRange.length - 1];

  if (currentPage === 0 || paginationRange === undefined || paginationRange.length < MIN_PAGINATION_RANGE_LENGTH) {
    return null;
  }

  const displayNextButton = currentPage !== lastPage || !!hasMorePages;

  return (
    <PaginationContainerStyled>
      {currentPage !== 1 && (
        <PaginationItemStyled
          data-testid="pagination-previous"
          onClick={onPrevious}
          onKeyDown={(event) => handleKeyDown(event, currentPage - 1)}
          tabIndex={0}
          role="button"
          aria-label={t('pagination.previous')}
        >
          <ArrowIconStyled />
        </PaginationItemStyled>
      )}
      {paginationRange.map((pageNumber, index) => {
        const pageNumberLength = +pageNumber.toString().length;
        let itemSize = '$8';

        if (pageNumberLength > 3) {
          itemSize = '$12';
        }

        if (pageNumberLength === 3) {
          itemSize = '$10';
        }

        if (pageNumber === DOTS) {
          return (
            <PaginationItemStyled dots={true} key={index} aria-hidden="true">
              {DOTS}
            </PaginationItemStyled>
          );
        }
        return (
          <PaginationItemStyled
            css={{
              width: `${itemSize}`,
              height: `${itemSize}`,
            }}
            data-testid={`pagination-number-${pageNumber}`}
            selected={pageNumber === currentPage}
            key={index}
            onClick={() => onPageChange(pageNumber as number)}
            onKeyDown={(event) => handleKeyDown(event, pageNumber as number)}
            tabIndex={0}
            role="button"
            aria-label={t('pagination.page_number', { pageNumber })}
          >
            {pageNumber}
          </PaginationItemStyled>
        );
      })}
      {displayNextButton && (
        <PaginationItemStyled
          data-testid="pagination-next"
          onClick={onNext}
          onKeyDown={(event) => handleKeyDown(event, currentPage + 1)}
          tabIndex={0}
          role="button"
          aria-label={t('pagination.next')}
        >
          <ArrowIconStyled variant={'right'} />
        </PaginationItemStyled>
      )}
    </PaginationContainerStyled>
  );
};

export default Pagination;
